import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, getCurrentBrowserAuthHeaders } from "@/lib/api";
import type { PMClientAccount } from "@/types/ailixPM";

const STORAGE_KEY = "pm_client_session";

interface PMClientSession {
  token: string;
  client: {
    id: string;
    email: string;
    name: string;
    company_name?: string;
  };
  expiresAt: string;
}

interface UsePMClientAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  client: PMClientSession["client"] | null;
  login: (email: string, password?: string) => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  logout: () => void;
  getSessionToken: () => string | null;
}

export function usePMClientAuth(): UsePMClientAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [client, setClient] = useState<PMClientSession["client"] | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Load session from storage
  const loadSession = useCallback((): PMClientSession | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      const session: PMClientSession = JSON.parse(stored);

      // Check expiration
      if (new Date(session.expiresAt) < new Date()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return session;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, []);

  // Save session to storage
  const saveSession = useCallback((session: PMClientSession) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }, []);

  // Handle magic link callback
  const handleMagicLinkCallback = useCallback(async () => {
    const token = searchParams.get("token");
    const email = searchParams.get("email");

    if (!token || !email) return false;

    try {
      const response = await apiFetch("/v1/pm/client/auth/validate-magic-link", {
        method: "POST",
        headers: getCurrentBrowserAuthHeaders({
          fallbackClientEmail: email,
        }),
        body: JSON.stringify({ token, email }),
      });

      if (response.success && response.session_token) {
        const session: PMClientSession = {
          token: response.session_token,
          client: response.client,
          expiresAt: response.expires_at,
        };
        saveSession(session);
        setClient(session.client);
        setIsAuthenticated(true);

        // Clear URL params
        navigate("/pm/dashboard", { replace: true });
        return true;
      }
    } catch (error) {
      console.error("Magic link validation failed:", error);
    }
    return false;
  }, [searchParams, navigate, saveSession]);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);

      // Check for magic link callback
      const handledCallback = await handleMagicLinkCallback();
      if (handledCallback) {
        setIsLoading(false);
        return;
      }

      // Check existing session
      const session = loadSession();
      if (session) {
        setClient(session.client);
        setIsAuthenticated(true);
      }

      setIsLoading(false);
    };

    initAuth();
  }, [handleMagicLinkCallback, loadSession]);

  // Send magic link
  const sendMagicLink = useCallback(async (email: string) => {
    await apiFetch("/v1/pm/client/auth/send-magic-link", {
      method: "POST",
      headers: getCurrentBrowserAuthHeaders({
        fallbackClientEmail: email,
      }),
      body: JSON.stringify({ email }),
    });
  }, []);

  // Login with password
  const login = useCallback(
    async (email: string, password?: string) => {
      if (!password) {
        await sendMagicLink(email);
        return;
      }

      const response = await apiFetch("/v1/pm/client/auth/login", {
        method: "POST",
        headers: getCurrentBrowserAuthHeaders({
          fallbackClientEmail: email,
        }),
        body: JSON.stringify({ email, password }),
      });

      if (response.success && response.session_token) {
        const session: PMClientSession = {
          token: response.session_token,
          client: response.client,
          expiresAt: response.expires_at,
        };
        saveSession(session);
        setClient(session.client);
        setIsAuthenticated(true);
        navigate("/pm/dashboard");
      }
    },
    [sendMagicLink, saveSession, navigate]
  );

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setClient(null);
    setIsAuthenticated(false);
    navigate("/pm/login");
  }, [navigate]);

  // Get session token for API calls
  const getSessionToken = useCallback((): string | null => {
    const session = loadSession();
    return session?.token || null;
  }, [loadSession]);

  return {
    isAuthenticated,
    isLoading,
    client,
    login,
    sendMagicLink,
    logout,
    getSessionToken,
  };
}

// Auth wrapper component
export function PMClientAuthWrapper({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = usePMClientAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/pm/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
